import React from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Chip,
    Typography,
    Avatar,
    Tooltip,
    Rating,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { inkColors } from './constants/inkColors';
import { rarityColors } from './constants/rarityColors';
import CardPreviewCell from './CardPreviewCell';

const TierlistView = ({ cards, onCardHover, onCardLeave, onCardClick }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Definicja tierów z ratingami malejąco
    const tiers = [
        { rating: 5.0, color: '#ff4444', name: '5.0' },
        { rating: 4.5, color: '#ff8844', name: '4.5' },
        { rating: 4.0, color: '#ffcc44', name: '4.0' },
        { rating: 3.5, color: '#44ff44', name: '3.5' },
        { rating: 3.0, color: '#44ccff', name: '3.0' },
        { rating: 2.5, color: '#8844ff', name: '2.5' },
        { rating: 2.0, color: '#ff44cc', name: '2.0' },
        { rating: 1.5, color: '#ff4488', name: '1.5' },
        { rating: 1.0, color: '#888888', name: '1.0' },
        { rating: 0.5, color: '#666666', name: '0.5' }
    ];

    // Funkcja do grupowania kart według oceny
    const groupCardsByRating = () => {
        const grouped = {};
        tiers.forEach(tier => {
            grouped[tier.rating] = [];
        });

        cards.forEach(card => {
            const rating = card.rating || 0;
            // Znajdź odpowiedni tier na podstawie ratingu
            let targetTier = tiers[tiers.length - 1]; // domyślnie najniższy tier

            for (let i = 0; i < tiers.length; i++) {
                if (rating >= tiers[i].rating) {
                    targetTier = tiers[i];
                    break;
                }
            }

            grouped[targetTier.rating].push(card);
        });

        return grouped;
    };

    const groupedCards = groupCardsByRating();

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px',
                overflow: 'hidden',
                p: 1
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {tiers.map((tier) => (
                    <Box
                        key={tier.rating}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'rgba(0, 0, 0, 0.15)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            minHeight: isMobile ? '80px' : '110px',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        {/* Tier indicator - left side */}
                        <Box
                            sx={{
                                position: 'relative',
                                minWidth: isMobile ? '32px' : '40px',
                                height: '100%',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '3px',
                                    backgroundColor: tier.color,
                                    borderRadius: '0 2px 2px 0'
                                }
                            }}
                        >
                            <Typography
                                sx={{
                                    color: tier.color,
                                    fontWeight: 700,
                                    fontSize: isMobile ? '0.75rem' : '0.85rem',
                                    letterSpacing: '-0.5px',
                                    transform: 'rotate(-90deg)',
                                    whiteSpace: 'nowrap',
                                    opacity: 0.9,
                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                }}
                            >
                                {tier.name}
                            </Typography>
                        </Box>

                        {/* Cards container */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.3,
                                p: 0.8,
                                flex: 1,
                                alignItems: 'center',
                                minHeight: '100%'
                            }}
                        >
                            {groupedCards[tier.rating].length > 0 ? (
                                groupedCards[tier.rating].map((card, index) => (
                                    <Box
                                        key={card.id}
                                        sx={{
                                            cursor: 'pointer',
                                            position: 'relative',
                                            '&:hover': {
                                                transform: 'scale(1.15)',
                                                transition: 'transform 0.2s ease',
                                                zIndex: 10,
                                                '&::after': {
                                                    content: '""',
                                                    position: 'absolute',
                                                    top: '-2px',
                                                    left: '-2px',
                                                    right: '-2px',
                                                    bottom: '-2px',
                                                    background: `linear-gradient(45deg, ${tier.color}, transparent)`,
                                                    borderRadius: '8px',
                                                    zIndex: -1,
                                                    opacity: 0.3
                                                }
                                            }
                                        }}
                                        onMouseEnter={(event) => onCardHover && onCardHover(card, event)}
                                        onMouseLeave={() => onCardLeave && onCardLeave()}
                                        onClick={() => onCardClick && onCardClick(card, index)}
                                    >
                                        <img
                                            src={card.image_urls && card.image_urls.length > 0 ? card.image_urls[0].url : card.image_url}
                                            alt={card.name}
                                            style={{
                                                width: isMobile ? 50 : 70,
                                                height: isMobile ? 70 : 98,
                                                objectFit: 'cover',
                                                borderRadius: '6px',
                                                border: '1px solid rgba(255, 255, 255, 0.15)',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
                                            }}
                                        />
                                    </Box>
                                ))
                            ) : (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'rgba(255, 255, 255, 0.4)',
                                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                                        fontStyle: 'italic',
                                        width: '100%',
                                        height: '100%'
                                    }}
                                >
                                    Brak kart
                                </Box>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
};

const LimitedTierlistTable = ({ cards, sortConfig, onSort, onCardHover, onCardLeave, onCardClick, viewMode = 'table' }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Jeśli wybrany jest widok tierlisty, zwróć TierlistView
    if (viewMode === 'tierlist') {
        return (
            <TierlistView
                cards={cards}
                onCardHover={onCardHover}
                onCardLeave={onCardLeave}
                onCardClick={onCardClick}
            />
        );
    }

    const getSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return null;
        }
        return sortConfig.direction === 'asc' ? '↑' : '↓';
    };

    const handleSort = (key) => {
        onSort(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    if (cards.length === 0) {
        return (
            <Paper
                elevation={0}
                sx={{
                    backgroundColor: 'rgba(17, 24, 39, 0.8)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '16px',
                    p: 4,
                    textAlign: 'center'
                }}
            >
                <Typography variant="h6" color="text.secondary">
                    Nie znaleziono kart pasujących do kryteriów wyszukiwania
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: 'rgba(17, 24, 39, 0.8)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(76, 29, 149, 0.3)',
                borderRadius: '16px',
                overflow: 'hidden'
            }}
        >
            <TableContainer sx={{
                maxWidth: '100%',
                overflowX: 'auto',
                '& .MuiTable-root': {
                    minWidth: isMobile ? 240 : 900
                }
            }}>
                <Table sx={{
                    tableLayout: 'fixed',
                    '& .MuiTableCell-root': {
                        padding: { xs: '8px 6px', sm: '12px 8px' },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }
                }}>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(76, 29, 149, 0.1)' }}>
                            <TableCell
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                    textAlign: 'center',
                                    width: { xs: '90px', sm: '130px' }
                                }}
                            >
                                Podgląd
                            </TableCell>
                            {/* Ukryj kolumnę # na mobilnych */}
                            {!isMobile && (
                                <TableCell
                                    onClick={() => handleSort('sort_number')}
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                        textAlign: 'center',
                                        width: { xs: '50px', sm: '60px' },
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                            transition: 'background-color 0.2s ease'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        # {getSortIcon('sort_number')}
                                    </Box>
                                </TableCell>
                            )}
                            {/* Ukryj kolumnę Nazwa na mobilnych */}
                            {!isMobile && (
                                <TableCell
                                    onClick={() => handleSort('name')}
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                            transition: 'background-color 0.2s ease'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Nazwa {getSortIcon('name')}
                                    </Box>
                                </TableCell>
                            )}
                            {/* Ukryj kolumnę Rzadkość na mobilnych */}
                            {!isMobile && (
                                <TableCell
                                    onClick={() => handleSort('rarity')}
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                        textAlign: 'center',
                                        width: { xs: '100px', sm: '120px' },
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                            transition: 'background-color 0.2s ease'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        Rzadkość {getSortIcon('rarity')}
                                    </Box>
                                </TableCell>
                            )}
                            {/* Ukryj kolumnę Kolor na mobilnych */}
                            {!isMobile && (
                                <TableCell
                                    onClick={() => handleSort('magic_ink_colors')}
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                        textAlign: 'center',
                                        width: { xs: '100px', sm: '120px' },
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                            transition: 'background-color 0.2s ease'
                                        }
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                        Kolor {getSortIcon('magic_ink_colors')}
                                    </Box>
                                </TableCell>
                            )}
                            <TableCell
                                onClick={() => handleSort('rating')}
                                sx={{
                                    color: 'white',
                                    fontWeight: 600,
                                    borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                    textAlign: 'center',
                                    width: { xs: '150px', sm: '120px' },
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(76, 29, 149, 0.2)',
                                        transition: 'background-color 0.2s ease'
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    Ocena {getSortIcon('rating')}
                                </Box>
                            </TableCell>
                            {/* Ukryj kolumnę Opis na mobilnych */}
                            {!isMobile && (
                                <TableCell
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                        textAlign: 'left',
                                        width: { xs: '200px', sm: '200px' }
                                    }}
                                >
                                    Opis
                                </TableCell>
                            )}
                            {/* Ukryj kolumnę Dodatek na mobilnych */}
                            {!isMobile && (
                                <TableCell
                                    sx={{
                                        color: 'white',
                                        fontWeight: 600,
                                        borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                                        textAlign: 'center',
                                        width: { xs: '120px', sm: '150px' }
                                    }}
                                >
                                    Dodatek
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cards.map((card, index) => (
                            <TableRow
                                key={card.id}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(76, 29, 149, 0.1)',
                                        transition: 'background-color 0.2s ease'
                                    }
                                }}
                            >
                                {/* Card preview */}
                                <TableCell sx={{ textAlign: 'center' }}>
                                    <CardPreviewCell
                                        card={card}
                                        onHover={onCardHover}
                                        onLeave={onCardLeave}
                                        onClick={onCardClick ? () => onCardClick(card, index) : undefined}
                                    />
                                </TableCell>

                                {/* Card identifier number - ukryj na mobilnych */}
                                {!isMobile && (
                                    <TableCell sx={{ color: 'white', textAlign: 'center', fontWeight: 500 }}>
                                        {card.card_identifier ? card.card_identifier.split('/')[0] : '-'}
                                    </TableCell>
                                )}

                                {/* Name and Subtitle - ukryj na mobilnych */}
                                {!isMobile && (
                                    <TableCell sx={{ color: 'white', fontWeight: 500 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {card.name}
                                            </Typography>
                                            {card.subtitle && (
                                                <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                                    {card.subtitle}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                )}

                                {/* Rarity - ukryj na mobilnych */}
                                {!isMobile && (
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Chip
                                            label={card.rarity === 'SPECIAL' && card.special_rarity_id ? `${card.rarity} (${card.special_rarity_id})` : card.rarity || '-'}
                                            size="small"
                                            sx={{
                                                background: card.rarity === 'SPECIAL'
                                                    ? 'linear-gradient(135deg, #8b0000, #dc143c, #ff1493)'
                                                    : card.rarity === 'SUPER'
                                                        ? 'linear-gradient(135deg, #a8a8a8, #c0c0c0, #d3d3d3)'
                                                        : card.rarity === 'LEGENDARY'
                                                            ? 'linear-gradient(135deg, #8b6914, #b8860b, #daa520)'
                                                            : card.rarity === 'RARE'
                                                                ? 'linear-gradient(135deg, #8b4513, #cd7f32, #d2691e)'
                                                                : (rarityColors[card.rarity] || '#6b7280'),
                                                color: card.rarity === 'UNCOMMON' ? '#374151' : 'white',
                                                fontWeight: 600,
                                                fontSize: '0.7rem',
                                                height: '24px',
                                                minWidth: 'fit-content',
                                                border: card.rarity === 'SPECIAL'
                                                    ? '1px solid #dc143c'
                                                    : card.rarity === 'SUPER'
                                                        ? '1px solid #e5e5e5'
                                                        : card.rarity === 'LEGENDARY'
                                                            ? '1px solid #daa520'
                                                            : card.rarity === 'RARE'
                                                                ? '1px solid #d2691e'
                                                                : 'none',
                                                ...(card.rarity === 'SPECIAL' && {
                                                    boxShadow: '0 0 4px rgba(220, 20, 60, 0.4)'
                                                }),
                                                ...(card.rarity === 'SUPER' && {
                                                    boxShadow: '0 0 4px rgba(192, 192, 192, 0.4)'
                                                }),
                                                ...(card.rarity === 'LEGENDARY' && {
                                                    boxShadow: '0 0 4px rgba(184, 134, 11, 0.4)'
                                                }),
                                                ...(card.rarity === 'RARE' && {
                                                    boxShadow: '0 0 3px rgba(205, 127, 50, 0.4)'
                                                })
                                            }}
                                        />
                                    </TableCell>
                                )}

                                {/* Ink colors - ukryj na mobilnych */}
                                {!isMobile && (
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        {card.magic_ink_colors && card.magic_ink_colors.length > 0 ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                                {card.magic_ink_colors.map((color, index) => (
                                                    inkColors[color] && (
                                                        <Tooltip key={index} title={inkColors[color].name}>
                                                            <Avatar
                                                                src={inkColors[color].icon}
                                                                sx={{
                                                                    width: 20,
                                                                    height: 20,
                                                                    backgroundColor: inkColors[color].color
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    )
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                -
                                            </Typography>
                                        )}
                                    </TableCell>
                                )}

                                {/* Rating */}
                                <TableCell sx={{
                                    textAlign: 'center',
                                    padding: { xs: '6px 2px', sm: '8px 6px' }
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        {/* Rating - gwiazdki i liczba */}
                                        <Box sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            gap: 0.5
                                        }}>
                                            <Rating
                                                value={card.rating || 0}
                                                precision={0.5}
                                                size="small"
                                                readOnly
                                                sx={{
                                                    '& .MuiRating-iconFilled': { color: 'rgb(245, 158, 11)' },
                                                    '& .MuiRating-iconHover': { color: 'rgb(245, 158, 11)' }
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                                {card.rating || 0}
                                            </Typography>
                                        </Box>

                                        {/* Description - tylko na mobilnych */}
                                        {isMobile && (
                                            <Box sx={{ textAlign: 'center', maxWidth: '160px' }}>
                                                {card.description ? (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            color: 'white',
                                                            whiteSpace: 'normal',
                                                            wordWrap: 'break-word',
                                                            lineHeight: 1.3,
                                                            fontSize: '0.7rem'
                                                        }}
                                                    >
                                                        {card.description}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                                        Brak opisu
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>

                                {/* Description - tylko na desktopie */}
                                {!isMobile && (
                                    <TableCell>
                                        {card.description ? (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'white',
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    maxWidth: '180px',
                                                    lineHeight: 1.4
                                                }}
                                            >
                                                {card.description}
                                            </Typography>
                                        ) : (
                                            <Typography variant="caption" sx={{ color: 'rgb(156, 163, 175)' }}>
                                                Brak opisu
                                            </Typography>
                                        )}
                                    </TableCell>
                                )}

                                {/* Set - ukryj na mobilnych */}
                                {!isMobile && (
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                                            {card.set ? card.set.name : (card.card_sets ? card.card_sets.join(', ') : '-')}
                                        </Typography>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default LimitedTierlistTable; 